import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { API } from '../../lib/api';

const formatErr = (detail) => {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const STRENGTH_RULES = [
  { id: 'len', label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { id: 'upper', label: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'One number (0-9)', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character (!@#$%^&*…)', test: (p) => /[!@#$%^&*()\-_=+[\]{};:,.<>?/\\|`~]/.test(p) },
  { id: 'nospace', label: 'No spaces', test: (p) => p.length > 0 && !/\s/.test(p) },
];

const ChangePassword = () => {
  const { logout, authHeader, user } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const lastChanged = useMemo(() => {
    if (!user?.password_changed_at) return null;
    try {
      const d = new Date(user.password_changed_at);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch {
      return null;
    }
  }, [user]);

  const ruleResults = useMemo(
    () => STRENGTH_RULES.map((r) => ({ ...r, ok: r.test(newPassword) })),
    [newPassword]
  );
  const allRulesMet = ruleResults.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const sameAsCurrent = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    !!currentPassword && allRulesMet && passwordsMatch && !sameAsCurrent && !submitting && !success;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: authHeader() }
      );
      setSuccess('Password updated. Logging you out — please sign in again with your new password.');
      setTimeout(async () => {
        await logout();
        navigate('/admin/login', { replace: true });
      }, 2500);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <main className="max-w-md mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-500/15 border border-teal-500/30 mb-4">
            <Lock className="text-teal-300" size={26} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="change-password-title">
            Change Password
          </h1>
          <p className="text-slate-500 mt-2 text-sm">For your security, please use a strong password.</p>
          {lastChanged && (
            <p className="text-xs text-slate-500 mt-2" data-testid="last-changed">
              Last changed: <span className="text-slate-700 font-medium">{lastChanged}</span>
            </p>
          )}
        </div>

        <form
          onSubmit={submit}
          className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-7 shadow-2xl space-y-5"
          data-testid="change-password-form"
        >
          {/* Current */}
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-slate-400">Current password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-3 pr-11 text-slate-100 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition"
                autoComplete="current-password"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                tabIndex={-1}
                aria-label="Toggle current password visibility"
                data-testid="toggle-current-password"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New */}
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-slate-400">New password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-3 pr-11 text-slate-100 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition"
                autoComplete="new-password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                tabIndex={-1}
                aria-label="Toggle new password visibility"
                data-testid="toggle-new-password"
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <ul className="space-y-1 text-xs mt-2" data-testid="password-rules">
              {ruleResults.map((r) => (
                <li
                  key={r.id}
                  className={`flex items-center gap-2 ${r.ok ? 'text-emerald-300' : 'text-slate-500'}`}
                  data-testid={`rule-${r.id}`}
                >
                  {r.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                  {r.label}
                </li>
              ))}
              {sameAsCurrent && (
                <li className="flex items-center gap-2 text-amber-300" data-testid="rule-different">
                  <XCircle size={13} /> Must be different from current password
                </li>
              )}
            </ul>
          </div>

          {/* Confirm */}
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-slate-400">Confirm new password</label>
            <input
              type={showNew ? 'text' : 'password'}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full bg-slate-900/60 border rounded-lg px-4 py-3 text-slate-100 focus:outline-none focus:ring-1 transition ${
                confirmPassword.length === 0
                  ? 'border-white/10 focus:border-teal-400 focus:ring-teal-400'
                  : passwordsMatch
                  ? 'border-emerald-500/40 focus:border-emerald-400 focus:ring-emerald-400'
                  : 'border-red-500/40 focus:border-red-400 focus:ring-red-400'
              }`}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-red-300" data-testid="passwords-mismatch">Passwords don't match.</p>
            )}
          </div>

          {error && (
            <div
              className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2"
              data-testid="change-password-success"
            >
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            data-testid="change-password-submit"
          >
            {submitting && <Loader2 className="animate-spin" size={18} />}
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default ChangePassword;
